/**
 * Form rules checked before asking the server (pure; tested in test/forms.test.ts).
 *
 * The JOIN field: room codes are 5 characters from an alphabet without look-alikes
 * (`ROOM_CODE_ALPHABET`), so what the player types is upper-cased, separators are dropped,
 * and a character that can never be in a code is refused with a friendly word instead of
 * a server error. Sign-in / sign-up: the obvious mistakes, in words.
 */
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '@sa/protocol';
import { MIN_PASSWORD } from '../net/api';

export interface CodeInput {
  /** the cleaned code so far (at most ROOM_CODE_LENGTH valid characters) */
  code: string;
  /** a typed character that is never in a code (O, 0, I, 1, S, 5, …), or null */
  bad: string | null;
}

export function cleanCode(raw: string): CodeInput {
  let code = '';
  let bad: string | null = null;
  for (const ch of raw.toUpperCase()) {
    if (/[\s\-_.·]/.test(ch)) continue;
    if (ROOM_CODE_ALPHABET.includes(ch)) {
      if (code.length < ROOM_CODE_LENGTH) code += ch;
    } else bad ??= ch;
  }
  return { code, bad };
}

/** The hint for a refused character, e.g. `NO "O" IN ROOM CODES`. */
export function badCharText(ch: string): string {
  return `NO "${ch}" IN ROOM CODES`;
}

export const isFullCode = (code: string): boolean => code.length === ROOM_CODE_LENGTH;

/** What is wrong with the sign-in / sign-up form before asking the server, or null. */
export function formProblem(signUp: boolean, name: string, email: string, password: string): string | null {
  if (signUp && (name.length < 2 || name.length > 16 || !/^[A-Za-z0-9][A-Za-z0-9 _.-]*$/.test(name))) return 'CALLSIGN · 2–16 LETTERS OR DIGITS';
  if (!email || !password) return 'EMAIL AND PASSWORD, PLEASE';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return 'THAT EMAIL DOES NOT LOOK RIGHT';
  if (signUp && password.length < MIN_PASSWORD) return `PASSWORD · ${MIN_PASSWORD}+ CHARACTERS`;
  return null;
}
